'use client';

import { useState, useEffect } from 'react';
import PlanSelector from './PlanSelector';
import CheckoutForm from './CheckoutForm';
import OrderSummary from './OrderSummary';
import TrustSignals from './TrustSignals';
import SuccessModal from './SuccessModal';

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  cards: number;
  popular?: boolean;
  savings?: string;
  features: string[];
  billingCycle?: 'HALF_YEARLY' | 'YEARLY';
  isCustom?: boolean;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  vehicleNumber: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  paymentMethod: string;
}

export default function GetStartedInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedPlan, setSelectedPlan] =
    useState<(Plan & { quantity: number }) | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    orderId: '',
    email: '',
    estimatedDelivery: '',
    paymentUrl: undefined as string | undefined,
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-white pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto animate-pulse space-y-8">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 h-96 bg-gray-200 rounded-lg" />
              <div className="h-64 bg-gray-200 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handlePlanSelect = (plan: Plan, quantity: number) => {
    setSelectedPlan({ ...plan, quantity });
  };

  // One-time physical card cost for first purchase.
  const cardCost = selectedPlan ? 99 : 0;

  const handleFormSubmit = (formData: FormData) => {
    if (!selectedPlan) {
      return;
    }
    if (selectedPlan.isCustom || selectedPlan.id === 'pro') {
      alert('ParkPing Pro is for societies and enterprise. Please contact support@parkping.co.in.');
      return;
    }

    const planMap: Record<string, string> = {
      plus: 'INDIVIDUAL',
      prime: 'FAMILY',
      pro: 'PREMIUM',
    };

    const backendPlan = planMap[selectedPlan.id] || 'INDIVIDUAL';

    const deliveryDate = new Date();
    deliveryDate.setDate(deliveryDate.getDate() + 5);

    const payload = {
      name: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      vehicle: formData.vehicleNumber?.trim() || 'NA',
      plan: backendPlan,
      validityMonths: selectedPlan.billingCycle === 'HALF_YEARLY' ? 6 : 12,
      amount: selectedPlan.price * selectedPlan.quantity + cardCost,
    };

    setIsProcessing(true);

    fetch('/api/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          const message =
            data?.error ||
            'Failed to initialize payment. Please try again in a moment.';
          throw new Error(message);
        }
        return data as { applicationId?: string; paymentUrl?: string };
      })
      .then((data) => {
        setOrderDetails({
          orderId: data?.applicationId || `PKP${Date.now().toString().slice(-8)}`,
          email: formData.email,
          estimatedDelivery: deliveryDate.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          }),
          paymentUrl: data?.paymentUrl,
        });

        setShowSuccess(true);
      })
      .catch((error) => {
        alert(error?.message || 'Something went wrong. Please try again.');
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-[#f5fbf6] to-background pt-24 pb-16 mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">

            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold text-primary mb-4">
                Get Started with ParkPing
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Select a plan, add vehicle details, and activate private parking communication in minutes.
              </p>
            </div>

            <div className="mb-12">
              <TrustSignals />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {/* Left */}
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-subtle">
                  <PlanSelector onPlanSelect={handlePlanSelect} />
                </div>

                <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-subtle">
                  <CheckoutForm
                    onSubmit={handleFormSubmit}
                    isProcessing={isProcessing}
                  />
                </div>
              </div>

              {/* Right */}
              <div className="space-y-6 lg:sticky lg:top-28">
                <OrderSummary
                  selectedPlan={selectedPlan}
                  deliveryCharge={cardCost}
                />

                {/* Help */}
                <div className="bg-card rounded-2xl p-5 border border-border shadow-subtle">
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    Need Assistance?
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Our support team is here for orders, payments, and activation.
                  </p>
                  <a
                    href="mailto:support@parkping.co.in"
                    className="text-xs text-accent hover:underline"
                  >
                    support@parkping.co.in
                  </a>
                </div>

                {/* Why ParkPing */}
                <div className="bg-card rounded-2xl p-5 border border-border shadow-subtle">
                  <h4 className="text-sm font-semibold text-foreground mb-3">
                    Why ParkPing?
                  </h4>
                  <ul className="space-y-2 text-xs text-muted-foreground">
                    {[
                      'Private contact protection for vehicle owners',
                      'Privacy-first call routing system',
                      'Up to 15 alerts per plan',
                      'Fast activation with clear onboarding',
                      'Dedicated support for Pro customers'
                    ].map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="text-accent font-bold">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Bulk CTA */}
            <div className="mt-12 bg-accent/10 rounded-2xl p-6 border border-accent/20">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    Looking for Society or Corporate Plans?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Custom pricing for apartments, societies & enterprises.
                  </p>
                </div>
                <a
                  href="mailto:support@parkping.co.in"
                  className="px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 transition"
                >
                  Talk to Our Team
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>

      <SuccessModal isOpen={showSuccess} orderDetails={orderDetails} />
    </>
  );
}
